// 桌面拖拽 utility(spec 260508-folder-feature §5.5 / plan §5.1)。
//
// 职责:
//   - 序列化 / 反序列化 dataTransfer payload(双写 application/x-blade-desktop +
//     text/plain,读取优先前者)。
//   - 命中目标识别:document.elementFromPoint 沿 DOM 上溯找 data-desktop-kind /
//     data-folder-id / data-app-id;空白区域用网格公式算 index。
//   - 桌面排序辅助:reorder 函数处理"先 splice 后插入"时的 -1 修正。
//   - 文件夹默认名生成:nextFolderName 从已存在的 folder 名里取下一个序号。
//
// 与现有 CSS / Desktop.tsx / FolderIcon.tsx 的接口约定:
//   - data-desktop-kind 在 FolderIcon 上为 "folder"(其余 App 没标 kind,通过
//     data-app-id 判定)。
//   - .os-desktop-grid 是桌面 items 容器;空白区域命中时返回 kind='blank'。
//   - 网格常量 ICON_SIZE=96 / GRID_GAP=8 与 css 中 .os-icon / .os-desktop-grid
//     保持一致;改尺寸时同步两侧。

import type {
  DesktopFolderDTO,
  DesktopItemDTO,
  DesktopLayoutDTO,
} from '@blade/os-sdk';

// ---------------------------------------------------------------------------
// 类型
// ---------------------------------------------------------------------------

/** 拖动起源 + 类型,序列化进 dataTransfer。 */
export type DragPayload =
  | { source: 'desktop'; itemType: 'app'; appId: string }
  | { source: 'desktop'; itemType: 'folder'; folderId: string }
  | { source: 'folder'; folderId: string; appId: string };

export type DropTarget =
  /** 桌面上的某个 App 图标。 */
  | { kind: 'app'; appId: string }
  /** 桌面上的某个 folder 图标。 */
  | { kind: 'folder'; folderId: string }
  /** 桌面网格内的空白区域;index 表示插入到 items[] 哪个位置(可能 == length 表示末尾)。 */
  | { kind: 'blank'; index: number }
  /** 弹层内空白(用户没拖到具体目标)。 */
  | { kind: 'popover-blank' }
  /** 落点不在桌面也不在弹层(浏览器外 / 其他面板)。 */
  | { kind: 'none' };

// ---------------------------------------------------------------------------
// 常量
// ---------------------------------------------------------------------------

export const MIME_PRIMARY = 'application/x-blade-desktop';
export const MIME_FALLBACK = 'text/plain';

let activeDragPayload: DragPayload | null = null;

// 与 .os-icon / .os-desktop-grid 保持一致(spec design §5.5)。
const ICON_SIZE = 96;
const GRID_GAP = 8;

// ---------------------------------------------------------------------------
// 序列化
// ---------------------------------------------------------------------------

export function serializePayload(
  dt: DataTransfer,
  payload: DragPayload,
): void {
  activeDragPayload = payload;
  const json = JSON.stringify(payload);
  // 双写:application/x-blade-desktop 是首选,text/plain 给某些浏览器 fallback。
  // jsdom 对 setData('application/...') 在某些版本会抛错,catch 一下不影响。
  try {
    dt.setData(MIME_PRIMARY, json);
  } catch {
    /* ignore */
  }
  try {
    dt.setData(MIME_FALLBACK, json);
  } catch {
    /* ignore */
  }
  dt.effectAllowed = 'move';
}

export function getActiveDragPayload(): DragPayload | null {
  return activeDragPayload;
}

export function clearActiveDragPayload(): void {
  activeDragPayload = null;
}

export function deserializePayload(dt: DataTransfer): DragPayload | null {
  let raw = '';
  try {
    raw = dt.getData(MIME_PRIMARY);
  } catch {
    /* ignore */
  }
  if (!raw) {
    try {
      raw = dt.getData(MIME_FALLBACK);
    } catch {
      /* ignore */
    }
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as DragPayload;
    if (!parsed || typeof parsed !== 'object' || !('source' in parsed)) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// 命中目标
// ---------------------------------------------------------------------------

/**
 * 从 (clientX, clientY) 找到最近的桌面元素并返回 DropTarget。
 *
 * 规则:
 *   1. 优先沿 DOM 上溯找带 data-folder-id 的元素 → folder(包括桌面 folder
 *      图标和弹层 panel 自身)。
 *   2. 再找带 data-app-id 的元素 → app。
 *   3. 都不命中但落在弹层 panel 内 → popover-blank。
 *   4. 落在桌面网格(.os-desktop-grid)空白处 → blank + index(由网格公式计算)。
 *   5. 其他位置(任务栏 / 浏览器外) → none。
 *
 * **注意**:这里只是"哪个目标"判断,不解释"是否合法"——禁止动作的语义检查
 * 由 dispatcher(Desktop.tsx 中)做。
 */
export function findDropTarget(
  clientX: number,
  clientY: number,
  layout: DesktopLayoutDTO,
): DropTarget {
  if (typeof document === 'undefined') return { kind: 'none' };
  let el = document.elementFromPoint(clientX, clientY);
  if (!el) return { kind: 'none' };

  // 弹层 backdrop 覆盖整个屏幕,拖拽落点命中它时需要穿透到下方的桌面网格。
  // 暂时隐藏 backdrop 再做一次 elementFromPoint 获取真实目标。
  const backdrop = el.closest<HTMLElement>('.os-folder-popover-backdrop');
  if (backdrop && !el.closest('.os-folder-popover-panel')) {
    backdrop.style.pointerEvents = 'none';
    el = document.elementFromPoint(clientX, clientY);
    backdrop.style.pointerEvents = '';
    if (!el) return { kind: 'none' };
  }

  // 弹层内的 App(用 data-app-id 标记;DesktopAppIcon 渲染时会带)
  // 注意:弹层 panel 自己也带 data-folder-id,所以"folder 命中"要先排除
  // 弹层 panel(它不是桌面 folder 图标)。
  const folderEl = el.closest<HTMLElement>('[data-folder-id]');
  const appEl = el.closest<HTMLElement>('[data-app-id]');

  if (appEl) {
    // 弹层内的 app 命中:不视为桌面 app,因为它实际是文件夹成员;
    // 由 dispatcher 用 source 区分(从弹层拖出 → 落在桌面 app 上 → 拒绝)。
    // 这里仍然返回 {kind:'app'},但调用方用 `source==='folder'` 直接拒绝。
    return { kind: 'app', appId: appEl.dataset.appId ?? '' };
  }

  if (folderEl) {
    const kind = folderEl.dataset.desktopKind;
    if (kind === 'folder') {
      return {
        kind: 'folder',
        folderId: folderEl.dataset.folderId ?? '',
      };
    }
    // 弹层 panel 自身也有 data-folder-id 但 没有 desktop-kind。
    return { kind: 'popover-blank' };
  }

  const grid = el.closest<HTMLElement>('.os-desktop-grid');
  if (!grid) return { kind: 'none' };

  // 桌面网格空白 → 计算 insert index。
  //
  // 两个坑:
  // 1. 网格是 ``grid-auto-flow: column``(先填满一列再换下一列),所以线性下标
  //    是 ``col * rows + row``,不是行优先的 ``row * cols + col``。之前按行优先
  //    算,落点索引整个错位,拖到空白处重排等于没反应。
  // 2. 行高不再等于 ICON_SIZE + GRID_GAP —— css 用 ``minmax(96px, 1fr)`` 让轨道
  //    撑开吃掉容器余量(避免底部留白),实际行高会大于 96。所以优先从
  //    ``gridTemplateRows/Columns`` 读真实轨道数与尺寸,读不到再退回常量估算
  //    (jsdom 下 computedStyle 拿不到 used value,测试走的就是回退分支)。
  const rect = grid.getBoundingClientRect();
  const x = clientX - rect.left;
  const y = clientY - rect.top;

  const tracks = (value: string): number[] =>
    value
      .split(' ')
      .map((t) => Number.parseFloat(t))
      .filter((n) => Number.isFinite(n) && n > 0);

  let colTracks: number[] = [];
  let rowTracks: number[] = [];
  if (typeof getComputedStyle === 'function') {
    const cs = getComputedStyle(grid);
    colTracks = tracks(cs.gridTemplateColumns);
    rowTracks = tracks(cs.gridTemplateRows);
  }

  const indexFromTracks = (pos: number, sizes: number[]): number => {
    let acc = 0;
    for (let i = 0; i < sizes.length; i += 1) {
      acc += (sizes[i] ?? ICON_SIZE) + GRID_GAP;
      if (pos < acc) return i;
    }
    return Math.max(0, sizes.length - 1);
  };

  let cols: number;
  let rows: number;
  let col: number;
  let row: number;
  if (colTracks.length && rowTracks.length) {
    cols = colTracks.length;
    rows = rowTracks.length;
    col = indexFromTracks(x, colTracks);
    row = indexFromTracks(y, rowTracks);
  } else {
    const cell = ICON_SIZE + GRID_GAP;
    cols = Math.max(1, Math.floor((rect.width + GRID_GAP) / cell));
    rows = Math.max(1, Math.floor((rect.height + GRID_GAP) / cell));
    col = Math.max(0, Math.min(cols - 1, Math.floor(x / cell)));
    row = Math.max(0, Math.min(rows - 1, Math.floor(y / cell)));
  }

  // 上限取 items.length(允许插入到末尾;clamp 上限是 length 而非 length-1,
  // 与 design §5.5 N 条对齐)。
  const target = Math.max(
    0,
    Math.min(layout.items.length, col * rows + row),
  );
  return { kind: 'blank', index: target };
}

// ---------------------------------------------------------------------------
// 桌面排序
// ---------------------------------------------------------------------------

/**
 * 把 ``items[fromIdx]`` 移到 ``target`` 位置。
 *
 * 关键修正:当源在前(``fromIdx < target``)时,先 splice(fromIdx,1) 会让
 * target 之后的元素整体前移 1,直接 splice(target,0,item) 会让 item 插到
 * 用户预期位置之后一格。因此做 ``insertAt = fromIdx < target ? target - 1 : target``。
 *
 * 落到自身位置 → noop(返回原数组的浅 clone)。
 */
export function reorderDesktopItems(
  items: DesktopItemDTO[],
  fromIdx: number,
  target: number,
): DesktopItemDTO[] {
  if (fromIdx < 0 || fromIdx >= items.length) return items.slice();
  // target 从 [0, items.length] 取值;先钳一下兼容超出输入。
  const t = Math.max(0, Math.min(items.length, target));
  if (t === fromIdx || t === fromIdx + 1) {
    // 落在自身位置 → 不动。t === fromIdx + 1 也视为不动:用户拖到下一格的
    // 落点,经过 -1 修正会变成原位,直接 short-circuit 更直白。
    return items.slice();
  }
  const next = items.slice();
  const [picked] = next.splice(fromIdx, 1);
  if (picked === undefined) return items.slice();
  const insertAt = fromIdx < t ? t - 1 : t;
  next.splice(insertAt, 0, picked);
  return next;
}

// ---------------------------------------------------------------------------
// 文件夹默认名
// ---------------------------------------------------------------------------

/**
 * 在 ``layout.folders`` 内挑一个不冲突的默认名。
 *
 * 已存在 base → "base 2";已存在 base + "base 2" → "base 3"。命中跳过即可,
 * 不要求连续。
 */
export function nextFolderName(layout: DesktopLayoutDTO, base: string): string {
  const taken = new Set(layout.folders.map((f) => f.name));
  if (!taken.has(base)) return base;
  for (let i = 2; i < 1000; i += 1) {
    const candidate = `${base} ${i}`;
    if (!taken.has(candidate)) return candidate;
  }
  // 极端情况兜底:加个时间戳,几乎不会撞。
  return `${base} ${Date.now()}`;
}

// ---------------------------------------------------------------------------
// 工具:在 layout.items 中找 source 的下标
// ---------------------------------------------------------------------------

export function findDesktopAppIndex(
  layout: DesktopLayoutDTO,
  appId: string,
): number {
  return layout.items.findIndex(
    (it) => it.type === 'app' && it.appId === appId,
  );
}

export function findDesktopFolderIndex(
  layout: DesktopLayoutDTO,
  folderId: string,
): number {
  return layout.items.findIndex(
    (it) => it.type === 'folder' && it.folderId === folderId,
  );
}

export function findFolderById(
  layout: DesktopLayoutDTO,
  folderId: string,
): DesktopFolderDTO | undefined {
  return layout.folders.find((f) => f.id === folderId);
}
